/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BlogTagFragment } from "@cosmos/hygraph-schema";

import { CosmosTag, CosmosTagProps, CosmosTagSizeEnum, CosmosText, CosmosTextVariantEnum } from "@components/elements";
import { css, cx } from "@panda/css";
import { useCosmosComponentContext } from "@components/context";

import { tagList } from "./TagList.style";

export type TagListProps = {
	className?: string;
	title?: string;
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	size?: CosmosTagSizeEnum;
};

const isBlogTag = (tag: CosmosTagProps | BlogTagFragment): tag is BlogTagFragment => {
	const bt = tag as BlogTagFragment;
	return Boolean(bt && bt.tagId);
};

export function TagList({ className, title, tags = [], size }: TagListProps): React.JSX.Element | null {
	const { feedRootPath = "/feed" } = useCosmosComponentContext();
	if (!tags?.length && !title) return null;

	return (
		<div className={cx("tag-list", tagList, className)}>
			{title ? (
				<CosmosText
					variant={CosmosTextVariantEnum.headingMD}
					as="h3"
					className={css({
						paddingEnd: {
							base: "eds.dimension.16",
							mdDown: "0",
						},
					})}
				>
					{title}
				</CosmosText>
			) : null}
			{tags?.length
				? tags.map((tag, i) => {
						if (!tag) return null;

						let href: string = "";
						if (isBlogTag(tag)) {
							href = `${feedRootPath}?tags=${tag.tagId}`;
							return <CosmosTag key={`${tag.label}-${i}`} size={size} href={href} label={tag?.label || ""} />;
						} else {
							href = tag?.href || "";
							return (
								<CosmosTag
									key={`${tag.label}-${i}`}
									size={size}
									href={href}
									label={tag?.label || ""}
									icon={tag?.icon}
									iconPosition={tag?.iconPosition}
								/>
							);
						}
					})
				: null}
		</div>
	);
}
